import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Kanban, 
  FolderOpen, 
  Receipt, 
  Users, 
  FileText,
  Settings,
  Map,
  CreditCard,
  Menu,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  LogOut,
  BarChart3,
  History,
  Handshake,
  FileSpreadsheet,
  Calculator,
  FolderArchive,
  ScrollText
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { qboApi } from '../../lib/api';

// Owner navigation items - isSuperOwner flag indicates super_owner only routes
const ownerNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Kanban, label: 'Pipeline', path: '/pipeline' },
  { icon: FolderOpen, label: 'Projects', path: '/projects' },
  { icon: Receipt, label: 'Transactions', path: '/transactions' },
  { icon: Users, label: 'Employee Ledger', path: '/ledger' },
  { icon: Users, label: 'Ownership', path: '/ownership' },
  { icon: CreditCard, label: 'Notes Receivable', path: '/notes' },
  { icon: FileSpreadsheet, label: 'Proposals', path: '/proposals' },
  { icon: Calculator, label: 'Deal Analysis', path: '/deal-analysis' },
  { icon: Handshake, label: 'Partners', path: '/partners' },
  { icon: FolderArchive, label: 'Documents', path: '/documents' },
  { icon: ScrollText, label: 'Templates', path: '/templates' },
  { icon: FileText, label: 'Reports', path: '/reports' },
  { icon: History, label: 'History', path: '/activity-history', superOwnerOnly: true },
];

// Investor navigation items
const investorNavItems = [
  { icon: TrendingUp, label: 'My Investments', path: '/investor' },
];

export const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isOwner, isInvestor, isSuperOwner, user, logout } = useAuth();
  const navigate = useNavigate();

  // Select navigation items based on role
  // Filter out super_owner only items for base owners
  const navItems = isOwner 
    ? ownerNavItems.filter(item => !item.superOwnerOnly || isSuperOwner)
    : investorNavItems;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu when route changes
  const handleNavClick = () => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  // ── QBO unmapped-txn badge (super_owner only) ───────────────────────
  // Polls /api/quickbooks/status every 60s. The count combines unresolved
  // failed sync mappings (transactions QBO couldn't auto-attribute to a
  // project) and unmapped QBO classes (new classes seen during sync that
  // have no LandFlow project mapping). Both surface on the QBO Sync page
  // and represent admin action items, so we badge them together.
  const [qboUnmappedCount, setQboUnmappedCount] = useState(0);
  useEffect(() => {
    if (!isSuperOwner) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await qboApi.getStatus();
        const d = res?.data || {};
        if (!cancelled) {
          setQboUnmappedCount((d.failed_mappings_count || 0) + (d.new_classes_count || 0));
        }
      } catch {
        // QBO not connected or transient error — keep last known count, do
        // not flash 0. The page itself surfaces connection state.
      }
    };
    refresh();
    const id = setInterval(refresh, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isSuperOwner]);

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-forest-800 text-white rounded-sm shadow-lg"
        data-testid="mobile-menu-toggle"
      >
        {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay */}
      {isMobile && isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "sidebar transition-all duration-300 ease-in-out z-40",
          isMobile ? (isMobileOpen ? "translate-x-0 w-56" : "-translate-x-full w-56") : "translate-x-0",
          isMobile && "fixed h-full"
        )}
        style={{ 
          width: isMobile ? (isMobileOpen ? '14rem' : '14rem') : (isCollapsed ? '4rem' : '14rem')
        }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={cn(
          "p-4 border-b border-white/10",
          isCollapsed && !isMobile && "px-2"
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              "bg-terracotta rounded-sm flex items-center justify-center flex-shrink-0",
              isCollapsed && !isMobile ? "w-10 h-10" : "w-10 h-10"
            )}>
              <Map className="w-6 h-6 text-white" />
            </div>
            {(!isCollapsed || isMobile) && (
              <div className="overflow-hidden">
                <h1 className="font-heading font-bold text-lg leading-tight whitespace-nowrap">LandFlow</h1>
                <p className="text-xs text-white/60 whitespace-nowrap">
                  {isInvestor ? 'Investor Portal' : 'Land Finder OS'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* User info for investors */}
        {isInvestor && !isCollapsed && (
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-xs text-white/40">Logged in as</p>
            <p className="text-sm text-white truncate">{user?.name || user?.email}</p>
            {user?.investor_name && (
              <p className="text-xs text-terracotta">{user.investor_name}</p>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className={cn("px-3 mb-2", isCollapsed && !isMobile && "px-1")}>
            {(!isCollapsed || isMobile) && (
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-medium">
                {isOwner ? 'Operations' : 'Portfolio'}
              </span>
            )}
          </div>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  "nav-item",
                  isActive && "active",
                  isCollapsed && !isMobile && "justify-center px-0 mx-1"
                )
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              title={isCollapsed && !isMobile ? item.label : undefined}
            >
              <item.icon className={cn("nav-item-icon", isCollapsed && !isMobile && "mr-0")} />
              {(!isCollapsed || isMobile) && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className={cn(
          "p-4 border-t border-white/10",
          isCollapsed && !isMobile && "px-1"
        )}>
          {/* QBO Sync - super_owner only */}
          {isSuperOwner && (
            <NavLink
              to="/qbo-sync"
              onClick={handleNavClick}
              className={cn(
                "nav-item rounded-sm",
                isCollapsed && !isMobile && "justify-center px-0"
              )}
              data-testid="nav-qbo-sync"
              title={isCollapsed && !isMobile ? `QBO Sync${qboUnmappedCount > 0 ? ` (${qboUnmappedCount} unmapped)` : ''}` : undefined}
            >
              <div className="relative">
                <RefreshCw className={cn("nav-item-icon", isCollapsed && !isMobile && "mr-0")} />
                {/* Collapsed-mode dot — keeps the warning visible when the
                    label is hidden so admins still see action items at a glance. */}
                {isCollapsed && !isMobile && qboUnmappedCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-1 ring-forest-900"
                    aria-label={`${qboUnmappedCount} unmapped`}
                    data-testid="nav-qbo-sync-badge-dot"
                  />
                )}
              </div>
              {(!isCollapsed || isMobile) && (
                <>
                  <span>QBO Sync</span>
                  {qboUnmappedCount > 0 && (
                    <span
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold text-white bg-red-500 rounded-full tabular-nums"
                      data-testid="nav-qbo-sync-badge"
                    >
                      {qboUnmappedCount > 99 ? '99+' : qboUnmappedCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )}

          {/* Settings - all users */}
          <NavLink
              to="/settings"
              onClick={handleNavClick}
              className={cn(
                "nav-item rounded-sm",
                isCollapsed && !isMobile && "justify-center px-0"
              )}
              data-testid="nav-settings"
              title={isCollapsed && !isMobile ? "Settings" : undefined}
            >
              <Settings className={cn("nav-item-icon", isCollapsed && !isMobile && "mr-0")} />
              {(!isCollapsed || isMobile) && <span>Settings</span>}
            </NavLink>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className={cn(
              "nav-item rounded-sm w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10",
              isCollapsed && !isMobile && "justify-center px-0"
            )}
            data-testid="logout-btn"
            title={isCollapsed && !isMobile ? "Log Out" : undefined}
          >
            <LogOut className={cn("nav-item-icon", isCollapsed && !isMobile && "mr-0")} />
            {(!isCollapsed || isMobile) && <span>Log Out</span>}
          </button>
          
          {/* Collapse toggle - desktop only */}
          {!isMobile && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="w-full mt-3 p-2 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-sm transition-colors"
              data-testid="sidebar-collapse-toggle"
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  <span className="text-xs">Collapse</span>
                </>
              )}
            </button>
          )}
          
          {(!isCollapsed || isMobile) && (
            <div className="mt-4 text-xs text-white/40 text-center">
              v2.0.0 • LandFlow
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
